import React, { useState, useEffect, useRef } from 'react';
import api, { errorMessage } from '../api';
import { useAuth } from '../context/AuthContext';
import AuthLayout from './AuthLayout';

export default function Login({ onSwitch }) {
  const { login } = useAuth();
  const [view, setView] = useState('login'); // 'login' | 'forgot' | 'reset'
  const [identifier, setIdentifier] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  // setBusy slår igenom först vid nästa rendering, så två snabba klick hinner
  // båda förbi knappens disabled. En ref stänger luckan direkt. Det spelar
  // roll här: varje ny återställningsbegäran ogiltigförklarar den förra, så
  // dubbelsändning ger två mail där bara det sista fungerar.
  const inFlight = useRef(false);

  // Länken i återställningsmailet öppnar appen med ?token=...
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    if (token) {
      setResetToken(token);
      setView('reset');
    }
  }, []);

  const clearUrl = () => window.history.replaceState({}, '', '/');

  const go = (next) => {
    // Aterstallning kraver e-post. Har anvandaren redan skrivit en adress i
    // inloggningsfaltet slipper hen skriva den en gang till.
    if (next === 'forgot' && !email && identifier.includes('@')) {
      setEmail(identifier.trim());
    }
    setView(next);
    setError('');
    setMessage('');
  };

  const submit = (handler) => async (event) => {
    event.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    setError('');
    setMessage('');
    setBusy(true);
    try {
      await handler();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const handleLogin = submit(async () => {
    await login(identifier.trim(), password);
  });

  const handleForgot = submit(async () => {
    const res = await api.post('/auth/forgot-password', { email: email.trim() });
    setMessage(res.data.message);
  });

  const handleReset = submit(async () => {
    const res = await api.post('/auth/reset-password', { token: resetToken, password: newPassword });
    setMessage(res.data.message);
    setTimeout(() => { go('login'); clearUrl(); }, 2000);
  });

  const feedback = (
    <>
      {error && <p className="notice notice--error">{error}</p>}
      {message && <p className="notice notice--success">{message}</p>}
    </>
  );

  if (view === 'reset') {
    return (
      <AuthLayout title="Nytt lösenord">
        <form className="auth-form" onSubmit={handleReset}>
          {feedback}
          <input
            type="password"
            placeholder="Nytt lösenord"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
          <p className="auth-form__hint">Minst 8 tecken.</p>
          <button className="btn btn--primary" type="submit" disabled={busy}>
            {busy ? 'Ändrar...' : 'Ändra lösenord'}
          </button>
          <button className="btn btn--outline" type="button" onClick={() => { go('login'); clearUrl(); }}>
            Tillbaka till inloggning
          </button>
        </form>
      </AuthLayout>
    );
  }

  if (view === 'forgot') {
    return (
      <AuthLayout title="Glömt lösenord">
        <form className="auth-form" onSubmit={handleForgot}>
          {feedback}
          <input
            type="email"
            placeholder="Din e-postadress"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
          <button className="btn btn--primary" type="submit" disabled={busy}>
            {busy ? 'Skickar...' : 'Skicka återställningslänk'}
          </button>
          <button className="btn btn--outline" type="button" onClick={() => go('login')}>
            Tillbaka till inloggning
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Logga in">
      <form className="auth-form" onSubmit={handleLogin}>
        {feedback}
        <input
          type="text"
          placeholder="E-post eller användarnamn"
          value={identifier}
          onChange={e => setIdentifier(e.target.value)}
          required
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck="false"
        />
        <input
          type="password"
          placeholder="Lösenord"
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
          autoComplete="current-password"
        />
        <button className="btn btn--primary" type="submit" disabled={busy}>
          {busy ? 'Loggar in...' : 'Logga in'}
        </button>
        <button className="btn btn--outline" type="button" onClick={() => go('forgot')}>
          Glömt lösenord
        </button>
        <button className="btn btn--outline" type="button" onClick={onSwitch}>
          Skapa konto
        </button>
      </form>
    </AuthLayout>
  );
}
